import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../App';
import Icon from '../components/Icon';
import './IW24Knowledge.css';

// Reine Ja-Antwort auf das Angebot „Schritte zeigen?" — nicht eine neue Frage,
// die zufällig mit „ja" beginnt („ja, aber wie buche ich…" matcht bewusst NICHT).
const YES_RE = /^(ja|jo|jep|gerne|gern|ja bitte|ja gerne|ja gern|bitte|zeig(e)?( mir)?|okay|ok|ja danke)[\s.]*$/i;

const API_URL = process.env.REACT_APP_API_URL || '/api';

const EXAMPLES = [
  'Wie lege ich eine Eigentümerversammlung an?',
  'Wie buche ich eine Rechnung?',
  'Wie schreibe ich eine Mahnung?',
  'Wie erstelle ich einen Wirtschaftsplan?',
];

function cleanText(value) {
  return (value || '').replace(/<\/?b>/g, '').replace(/\s+/g, ' ').trim();
}

// Quelle als Link (Support/Video) oder reiner Text (Handbuch hat keinen Deeplink).
function SourceItem({ source }) {
  const label = source.source;
  return source.url ? (
    <a className="iw24-kb__src" href={source.url} target="_blank" rel="noreferrer">
      <Icon name="document" size={13} />
      <span>{label}</span>
    </a>
  ) : (
    <span className="iw24-kb__src iw24-kb__src--plain">
      <Icon name="document" size={13} />
      <span>{label}</span>
    </span>
  );
}

function AssistantMessage({ msg, onDetail, onChoice, busy }) {
  const [showSources, setShowSources] = useState(false);
  const sources = msg.sources || [];

  // Rückfrage (Stufe 2): Bot grenzt verzweigende Themen per Multiple-Choice ein.
  // Dann gibt es noch keine Antwort -> kein Angebot, keine Quellen, nur Optionen.
  const clarify = msg.clarify;

  // Eskalation: (1) kurze Antwort -> (2) "Ausführlicher erklären"
  // -> (3) Originalquellen (eingeklapptes Dropdown, erst auf der ausführlichen Antwort).
  const showMoreBtn = msg.confident && !msg.detailed && !clarify;
  const showSourcesToggle = msg.confident && msg.detailed && sources.length > 0;

  return (
    <div className="iw24-kb__msg iw24-kb__msg--bot">
      <div className="iw24-kb__bubble">
        <div className="iw24-kb__answer-text">
          <ReactMarkdown>{msg.answer}</ReactMarkdown>
        </div>

        {clarify && (
          <div className="iw24-kb__choices">
            {clarify.options.map((opt, i) => (
              <button key={i} className="iw24-kb__choice" type="button" onClick={() => onChoice(opt)} disabled={busy}>
                {opt}
              </button>
            ))}
          </div>
        )}

        {showMoreBtn && (
          <div className="iw24-kb__offer">
            <p className="iw24-kb__offer-text">Möchten Sie, dass ich Ihnen die einzelnen Schritte zeige?</p>
            <button className="iw24-kb__more" type="button" onClick={() => onDetail(msg)} disabled={busy}>
              {busy ? 'Einen Moment…' : 'Ja, Schritte zeigen'}
            </button>
          </div>
        )}

        {showSourcesToggle && (
          <div className="iw24-kb__sources">
            <button
              className="iw24-kb__sources-toggle"
              type="button"
              aria-expanded={showSources}
              onClick={() => setShowSources((v) => !v)}
            >
              {showSources ? 'Originalquellen ausblenden' : 'Originalquellen in Immoware24 anzeigen'}
            </button>
            {showSources && sources.map((s, i) => (
              <SourceItem key={`${s.source_type}-${s.article_id || s.video_id || s.page_number}-${i}`} source={s} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function IW24Knowledge() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const threadRef = useRef(null);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  // Verlauf als Turns für den Backend-Kontext (letzte 8, Bot-Antwort = answer).
  function historyTurns() {
    return messages
      .slice(-8)
      .map((m) => (m.role === 'user'
        ? { role: 'user', content: m.text }
        : { role: 'assistant', content: m.answer }))
      .filter((t) => t.content);
  }

  async function ask(question, { detailed = false } = {}) {
    if (!token) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/iw24-knowledge/answer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: question, detailed, limit: 8, history: historyTurns() }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setMessages((prev) => [...prev, {
        role: 'assistant',
        answer: data.answer,
        sources: data.sources || [],
        confident: data.confident !== false,
        clarify: data.clarify || null,
        detailed,
        query: question,
      }]);
    } catch {
      setError('Der Immoware24-Assistent ist gerade nicht erreichbar.');
    } finally {
      setLoading(false);
    }
  }

  function submit(e) {
    e.preventDefault();
    const q = input.trim();
    if (q.length < 2 || loading) return;
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    setInput('');
    // „ja" auf das Schritte-Angebot der letzten Antwort -> ausführlich, statt neuer Suche.
    const lastBot = [...messages].reverse().find((m) => m.role === 'assistant');
    if (YES_RE.test(q) && lastBot && lastBot.confident && !lastBot.detailed) {
      ask(lastBot.query, { detailed: true });
      return;
    }
    ask(q);
  }

  function askExample(q) {
    if (loading) return;
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    ask(q);
  }

  // Klick auf eine Rückfrage-Option = verfeinerte Frage als neuer Turn.
  function askChoice(q) {
    if (loading) return;
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    ask(q);
  }

  const empty = messages.length === 0;

  return (
    <div className="iw24-kb">
      <div className="iw24-kb__header">
        <div>
          <div className="fleet-label">Immoware24</div>
          <h1>Immoware24-Assistent</h1>
        </div>
      </div>

      <div className="iw24-kb__chat">
        <div className="iw24-kb__thread" ref={threadRef}>
          {empty && (
            <div className="iw24-kb__welcome">
              <p>Fragen Sie zu Abläufen in Immoware24 — kurz und auf den Punkt. Für eine Schritt-für-Schritt-Anleitung können Sie danach „Ausführlicher erklären“ wählen.</p>
              <div className="iw24-kb__examples">
                {EXAMPLES.map((q) => (
                  <button key={q} type="button" onClick={() => askExample(q)}>{q}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            m.role === 'user' ? (
              <div className="iw24-kb__msg iw24-kb__msg--user" key={i}>
                <div className="iw24-kb__bubble">{m.text}</div>
              </div>
            ) : (
              <AssistantMessage key={i} msg={m} onDetail={(msg) => ask(msg.query, { detailed: true })} onChoice={askChoice} busy={loading} />
            )
          ))}

          {loading && (
            <div className="iw24-kb__msg iw24-kb__msg--bot">
              <div className="iw24-kb__bubble iw24-kb__typing"><span /><span /><span /></div>
            </div>
          )}
        </div>

        {error && <div className="iw24-kb__notice iw24-kb__notice--error">{error}</div>}

        <form className="iw24-kb__composer" onSubmit={submit}>
          <Icon name="search" size={18} />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ihre Frage zu Immoware24…"
            aria-label="Frage zu Immoware24"
          />
          <button type="submit" disabled={loading || input.trim().length < 2}>
            {loading ? '…' : 'Senden'}
          </button>
        </form>
      </div>
    </div>
  );
}
